import { type JSX } from 'react';

import { AccordionItemTrigger, EloIcon } from '@plurall/elo-v3';

import { NavLabelFactory } from './factories/local-navigation-entry-factory';
import {
  NavAccordionContentFactory,
  NavAccordionIndicatorFactory,
  NavAccordionItemFactory,
  NavAccordionTriggerFactory,
  NavSubitemFactory,
  NavSublistFactory,
} from './factories/local-navigation-submenu-factory';
import { LocalNavigationEntry } from './LocalNavigationEntry';
import { getChildLinks, isItemActive } from '../helpers/localNavigationItemHelpers';
import {
  getFilledIconName,
  getIconColor,
  isReactTarget,
  isRouteActive,
  useLocalNavigationEntry,
} from '../hooks/useLocalNavigationEntry';
import { type LocalNavigationMenuItem } from '../hooks/useLocalNavigationMenuSections';
import { type UtilityAction } from '../types/localNavigationActions';

type LocalNavigationSubmenuItemType = Extract<LocalNavigationMenuItem, { type: 'submenu' }>;

export type LocalNavigationSubmenuItemProps = {
  accordionKey: string;
  isExpanded: boolean;
  item: LocalNavigationSubmenuItemType;
  onAction?: (action: UtilityAction) => void;
  pathname: string;
};

export function LocalNavigationSubmenuItem({
  accordionKey,
  isExpanded,
  item,
  onAction,
  pathname,
}: LocalNavigationSubmenuItemProps): JSX.Element {
  const { isHovered, isHighlighted, sharedProps } = useLocalNavigationEntry(item, false);
  const isSubmenuActive = isItemActive(pathname, item);
  const iconColor = getIconColor();
  const resolvedIcon =
    (isHighlighted || isHovered ? getFilledIconName(item.icon) || item.icon : item.icon) ?? 'grid-squares';

  return (
    <NavAccordionItemFactory value={accordionKey}>
      <AccordionItemTrigger asChild>
        <NavAccordionTriggerFactory
          aria-expanded={isExpanded}
          isActive={isSubmenuActive}
          tabIndex={0}
          type='button'
          {...sharedProps}>
          <EloIcon
            color={iconColor}
            data-test-id='elo-icon'
            icon={resolvedIcon}
            size='lg'
          />

          <NavLabelFactory>{item.text}</NavLabelFactory>
          <NavAccordionIndicatorFactory>
            <EloIcon
              color={iconColor}
              data-test-id='elo-icon'
              icon={isExpanded ? 'arrow-tip-down-fill' : 'arrow-tip-down'}
              size='lg'
            />
          </NavAccordionIndicatorFactory>
        </NavAccordionTriggerFactory>
      </AccordionItemTrigger>
      <NavAccordionContentFactory>
        <NavSublistFactory>
          {getChildLinks(item).map((link, index) => {
            const isActive = isReactTarget(link.targetEnvironment) && isRouteActive(pathname, link);
            const linkKey = link.id ?? link.url ?? `link-${index}`;
            return (
              <NavSubitemFactory key={`${accordionKey}-${linkKey}`}>
                <LocalNavigationEntry
                  active={isActive}
                  className='local-navigation__subitem-link'
                  item={link}
                  onAction={onAction}
                />
              </NavSubitemFactory>
            );
          })}
        </NavSublistFactory>
      </NavAccordionContentFactory>
    </NavAccordionItemFactory>
  );
}
